"use client";

import React, { useState, useEffect, useRef } from "react";
import NextImage from "next/image";

interface ImageProps {
  src?: string;
  alt?: string;
  className?: string; // Sizing, border-radius, wrapper layout styles
  imgClassName?: string; // Image scaling/fit styles (e.g. object-contain, object-cover)
  fallbackSrc?: string;
  optimizeWidth?: number; // Target render width hint used for responsive srcset selection
  priority?: boolean; // Set true for above-the-fold/LCP images
}

export default function Image({
  src,
  alt = "",
  className = "",
  imgClassName = "object-cover",
  optimizeWidth = 800,
  priority = false,
  fallbackSrc = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23e5e5e5'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='10' fill='%23999'>Image Error</text></svg>",
}: ImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // If image is already complete in DOM cache upon mount/hydration, display immediately
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      requestAnimationFrame(() => setIsLoaded(true));
    }
  }, [src]);

  // Reset load/error state when the source changes
  const [renderedSrc, setRenderedSrc] = useState(src);
  if (src !== renderedSrc) {
    setRenderedSrc(src);
    setIsLoaded(false);
    setError(false);
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Real-World Skeleton Loader & Animated Spinner */}
      {!isLoaded && !error && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center overflow-hidden bg-slate-200/70 dark:bg-[#1E1916] transition-opacity duration-300 pointer-events-none select-none"
          aria-hidden="true"
        >
          {/* Shimmer Sweep Wave */}
          <div className="absolute inset-0 -translate-x-full animate-image-shimmer bg-gradient-to-r from-transparent via-white/40 dark:via-white/5 to-transparent pointer-events-none" />

          {/* Centered Modern Dual-Ring Brand Spinner */}
          <div className="relative flex flex-col items-center justify-center gap-2 z-10">
            <div className="relative w-8 h-8 sm:w-9 sm:h-9">
              {/* Outer track */}
              <div className="w-full h-full rounded-full border-2 border-slate-300/60 dark:border-white/10" />
              {/* Spinning gradient arc */}
              <div className="absolute inset-0 w-full h-full rounded-full border-2 border-transparent border-t-[#AA4C14] dark:border-t-[#FFB586] border-r-[#AA4C14] dark:border-r-[#FFB586] animate-spin" />
              {/* Pulsing center dot */}
              <div className="absolute inset-0 m-auto w-1.5 h-1.5 rounded-full bg-[#AA4C14] dark:bg-[#FFB586] animate-pulse" />
            </div>
            {/* Minimalist status badge */}
            <span className="font-label-code text-[10px] tracking-widest font-semibold uppercase text-slate-500 dark:text-[#A39890] opacity-80">
              Loading
            </span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="absolute inset-0 bg-neutral-200/80 dark:bg-[#25201D] flex flex-col items-center justify-center text-neutral-400 gap-1.5 p-2 z-10">
          <svg className="w-7 h-7 opacity-40 text-[#AA4C14]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span className="font-label-code text-[10px] uppercase font-bold tracking-wider opacity-60 text-center">Visual unavailable</span>
        </div>
      )}

      <NextImage
        ref={imgRef}
        src={error || !src ? fallbackSrc : src}
        alt={alt}
        fill
        sizes={`(max-width: 768px) 100vw, ${optimizeWidth}px`}
        priority={priority}
        unoptimized={error || !src}
        className={`transition-opacity duration-500 ease-in-out ${imgClassName} ${
          isLoaded ? "opacity-100" : "opacity-0"
        }`}
        onLoad={() => setIsLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}
